import { useContext, useEffect, useMemo, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import moment from "moment";
import { ArrowLeftOutlined, CalendarOutlined, CheckCircleOutlined } from "@ant-design/icons";
import { Button, Card, Col, Empty, Row, Spin, Statistic, Table, Typography } from "antd";
import { AppStateContext } from "../../context/AppContext";
import useRequest from "../../hooks/useRequest";
import apiPath from "../../constants/apiPath";

function isValidMonth(value) {
  return typeof value === "string" && /^\d{4}-\d{2}$/.test(value) && moment(`${value}-01`, "YYYY-MM-DD", true).isValid();
}

function formatDuration(totalMinutes) {
  const minutes = Math.max(0, Number(totalMinutes) || 0);
  const hours = Math.floor(minutes / 60);
  const remainingMinutes = minutes % 60;
  return hours > 0
    ? `${hours} hr${hours === 1 ? "" : "s"} ${remainingMinutes} min`
    : `${remainingMinutes} min`;
}

export default function UserAttendance() {
  const { userId } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { setPageHeading } = useContext(AppStateContext);
  const { request } = useRequest();
  const initialMonth = isValidMonth(searchParams.get("month"))
    ? searchParams.get("month")
    : moment().format("YYYY-MM");
  const [selectedMonth, setSelectedMonth] = useState(initialMonth);
  const [user, setUser] = useState(null);
  const [locations, setLocations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    setPageHeading("Monthly Attendance");
  }, [setPageHeading]);

  useEffect(() => {
    if (!userId) {
      setError("Invalid user");
      setLoading(false);
      return;
    }

    request({
      url: `${apiPath.getUserLocation}/${userId}`,
      method: "GET",
      onSuccess: (response) => {
        const data = response?.data || {};
        setUser(data.user || null);
        setLocations(Array.isArray(data.locations) ? data.locations : []);
        setError(null);
        setLoading(false);
      },
      onError: (err) => {
        setError(err?.message || "Failed to load attendance");
        setLoading(false);
      },
    });
  }, [userId]);

  const attendanceRows = useMemo(() => {
    const days = {};

    locations.forEach((location) => {
      if (!location.mailReceivedAt || !moment(location.mailReceivedAt).isValid()) return;
      const localTime = moment.utc(location.mailReceivedAt).local();
      if (localTime.format("YYYY-MM") !== selectedMonth) return;

      const date = localTime.format("YYYY-MM-DD");
      if (!days[date]) days[date] = [];
      days[date].push(localTime.valueOf());
    });

    return Object.entries(days)
      .sort(([first], [second]) => first.localeCompare(second))
      .map(([date, timestamps]) => {
        const sortedTimes = timestamps.sort((first, second) => first - second);
        const minutes = Math.floor((sortedTimes[sortedTimes.length - 1] - sortedTimes[0]) / 60000);
        return {
          key: date,
          date,
          day: moment(date).format("dddd"),
          firstTime: sortedTimes[0],
          lastTime: sortedTimes[sortedTimes.length - 1],
          minutes,
          records: sortedTimes.length,
        };
      });
  }, [locations, selectedMonth]);

  const month = moment(`${selectedMonth}-01`, "YYYY-MM-DD", true);
  const totalWorkingMinutes = attendanceRows.reduce((total, row) => total + row.minutes, 0);
  const presentDays = attendanceRows.length;
  const absentDays = Math.max(month.daysInMonth() - presentDays, 0);

  if (loading) {
    return <div style={{ minHeight: 320, display: "flex", justifyContent: "center", alignItems: "center" }}><Spin size="large" /></div>;
  }

  if (error || !user) {
    return (
      <Card>
        <Typography.Text type="danger">{error || "User not found"}</Typography.Text>
        <div style={{ marginTop: 12 }}>
          <Button type="primary" icon={<ArrowLeftOutlined />} onClick={() => navigate("/user-management")}>
            Back to User Management
          </Button>
        </div>
      </Card>
    );
  }

  return (
    <div>
      <Button type="text" icon={<ArrowLeftOutlined />} onClick={() => navigate(`/user-management/${userId}/location`)}>
        Back to Location
      </Button>

      <Card
        title={<span><CalendarOutlined /> Monthly Attendance</span>}
        style={{ marginTop: 12 }}
        extra={(
          <input
            type="month"
            value={selectedMonth}
            onChange={(event) => setSelectedMonth(event.target.value)}
            aria-label="Attendance month"
            style={{ height: 32, padding: "0 8px", border: "1px solid #d9d9d9", borderRadius: 6 }}
          />
        )}
      >
        <div style={{ marginBottom: 16 }}>
          <strong>{user.name || "Unnamed user"}</strong>
          <span style={{ marginLeft: 16, color: "#595959" }}>Mobile: {user.mobileNumber || "—"}</span>
        </div>

        <Row gutter={[12, 12]}>
          <Col xs={24} sm={8}><Card size="small"><Statistic title="Present days" value={presentDays} /></Card></Col>
          <Col xs={24} sm={8}><Card size="small"><Statistic title="Absent days" value={absentDays} /></Card></Col>
          <Col xs={24} sm={8}><Card size="small"><Statistic title="Total working time" value={formatDuration(totalWorkingMinutes)} /></Card></Col>
        </Row>

        {attendanceRows.length ? (
          <Table
            style={{ marginTop: 20 }}
            size="small"
            rowKey="key"
            dataSource={attendanceRows}
            pagination={{ pageSize: 31, hideOnSinglePage: true }}
            columns={[
              { title: "Date", dataIndex: "date", render: (value) => moment(value).format("DD MMM YYYY") },
              { title: "Day", dataIndex: "day" },
              { title: "Check-in", dataIndex: "firstTime", render: (value) => moment(value).format("hh:mm A") },
              { title: "Check-out", dataIndex: "lastTime", render: (value) => moment(value).format("hh:mm A") },
              { title: "Working time", dataIndex: "minutes", render: (value) => formatDuration(value) },
              { title: "Location records", dataIndex: "records" },
              {
                title: "Status",
                render: () => <span style={{ color: "#52c41a" }}><CheckCircleOutlined /> Present</span>,
              },
            ]}
          />
        ) : (
          <Empty style={{ marginTop: 32 }} description="No attendance recorded for this month" />
        )}
      </Card>
    </div>
  );
}
